import { motion } from 'framer-motion';
import type { AppointmentRow } from '@/hooks/useAppointments';
import { theme } from '@/lib/theme';
import { getNowCO } from '@/lib/dateUtils';

interface Props {
  date: Date;
  appointments: AppointmentRow[];
  onSelectAppointment: (appt: AppointmentRow) => void;
}

const HOUR_START = 8;
const HOUR_END = 20;
const SLOT_HEIGHT = 64; // px per hour

function timeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

function getStatusColor(status: string) {
  if (status === 'confirmed') return { bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.35)', text: '#34d399' };
  if (status === 'cancelled') return { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', text: '#f87171' };
  return { bg: 'rgba(201,168,76,0.15)', border: 'rgba(201,168,76,0.35)', text: '#c9a84c' };
}

export default function CalendarDayView({ date, appointments, onSelectAppointment }: Props) {
  const hours = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
  const totalMinutes = (HOUR_END - HOUR_START) * 60;

  const { dateStr: todayStr, hours: nowHours, minutes: nowMinutes } = getNowCO();
  const dayStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const dayAppointments = appointments.filter((a) => a.appointment_date === dayStr);

  const isToday = dayStr === todayStr;
  const currentMinutes = isToday ? nowHours * 60 + nowMinutes : -1;

  return (
    <div className="flex flex-col h-full">
      {/* Day header */}
      <div
        className="flex items-center gap-3 px-6 py-4 border-b"
        style={{ borderColor: theme.colors.border }}
      >
        <div
          className={`w-10 h-10 flex items-center justify-center rounded-xl font-bold text-lg ${
            isToday ? 'text-[#0a0a0a]' : 'text-white'
          }`}
          style={{ backgroundColor: isToday ? theme.colors.primary : theme.colors.bgBase }}
        >
          {date.getDate()}
        </div>
        <div>
          <p className="text-white text-sm font-semibold capitalize">
            {date.toLocaleDateString('es-CO', { weekday: 'long', month: 'long', year: 'numeric' })}
          </p>
          <p className="text-white/40 text-xs mt-0.5">
            {dayAppointments.length} cita{dayAppointments.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Time grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex">
          {/* Hour labels */}
          <div className="w-16 flex-shrink-0">
            {hours.map((hour) => (
              <div
                key={hour}
                className="flex items-start justify-end pr-3 text-white/25 text-xs font-mono"
                style={{ height: SLOT_HEIGHT }}
              >
                <span className="-mt-2">{hour}:00</span>
              </div>
            ))}
          </div>

          {/* Grid + appointments */}
          <div
            className="flex-1 relative border-l"
            style={{ borderColor: theme.colors.border, height: SLOT_HEIGHT * hours.length }}
          >
            {/* Hour lines */}
            {hours.map((hour) => (
              <div
                key={hour}
                className="absolute left-0 right-0 border-t"
                style={{
                  top: (hour - HOUR_START) * SLOT_HEIGHT,
                  borderColor: theme.colors.border,
                }}
              />
            ))}

            {/* Half-hour lines */}
            {hours.map((hour) => (
              <div
                key={`half-${hour}`}
                className="absolute left-0 right-0 border-t border-dashed"
                style={{
                  top: (hour - HOUR_START) * SLOT_HEIGHT + SLOT_HEIGHT / 2,
                  borderColor: 'rgba(255,255,255,0.04)',
                }}
              />
            ))}

            {/* Current time indicator */}
            {isToday && currentMinutes >= HOUR_START * 60 && currentMinutes <= HOUR_END * 60 && (
              <div
                className="absolute left-0 right-0 z-10 flex items-center"
                style={{ top: ((currentMinutes - HOUR_START * 60) / 60) * SLOT_HEIGHT }}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0 -ml-1"
                  style={{ backgroundColor: theme.colors.primary }}
                />
                <div
                  className="flex-1 h-px"
                  style={{ backgroundColor: theme.colors.primary }}
                />
              </div>
            )}

            {/* Appointments */}
            {dayAppointments.map((appt, idx) => {
              const startMin = timeToMinutes(appt.start_time);
              const endMin = timeToMinutes(appt.end_time);
              const top = ((startMin - HOUR_START * 60) / 60) * SLOT_HEIGHT;
              const height = Math.max(((endMin - startMin) / 60) * SLOT_HEIGHT, 32);
              const colors = getStatusColor(appt.status);

              return (
                <motion.button
                  key={appt.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  onClick={() => onSelectAppointment(appt)}
                  className="absolute left-2 right-2 rounded-lg px-3 py-2 text-left cursor-pointer transition-all hover:brightness-125 hover:scale-[1.01]"
                  style={{
                    top,
                    height,
                    backgroundColor: colors.bg,
                    border: `1px solid ${colors.border}`,
                    zIndex: 5,
                  }}
                >
                  <div className="flex items-start justify-between gap-1 h-full overflow-hidden">
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-xs font-semibold truncate leading-tight"
                        style={{ color: colors.text }}
                      >
                        {appt.client_name}
                      </p>
                      {height > 40 && (
                        <p className="text-white/45 text-xs truncate mt-0.5 leading-tight">
                          {appt.services?.name ?? '—'}
                        </p>
                      )}
                      {height > 56 && (
                        <p className="text-white/30 text-xs font-mono mt-0.5">
                          {appt.start_time.slice(0, 5)} – {appt.end_time.slice(0, 5)}
                        </p>
                      )}
                    </div>
                    <div
                      className="w-5 h-5 flex items-center justify-center rounded flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                    >
                      <i className="ri-arrow-right-s-line text-white/50 text-xs" />
                    </div>
                  </div>
                </motion.button>
              );
            })}

            {/* Empty state */}
            {dayAppointments.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full mx-auto mb-2" style={{ backgroundColor: theme.colors.bgBase }}>
                    <i className="ri-calendar-line text-white/20 text-lg" />
                  </div>
                  <p className="text-white/25 text-sm">Sin citas este día</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
